
import { Component, Vue } from 'vue-property-decorator'
import {
  CaneAudioLanguage, CaneAudioOverview, CaneAudioPack, CaneAudioPackKind, CaneAudioSlot, CaneAudioSpecialPack,
  createCaneAudioLanguage, getCaneAudioOverview, listCaneAudioPacks, updateCaneAudioLanguage,
  updateCaneAudioPack, uploadCaneAudioPack
} from '@/api/caneAudio'

const SLOTS: CaneAudioSlot[] = ['female', 'female2', 'male', 'male2']

@Component({ name: 'CaneAudioPacks' })
export default class extends Vue {
  private loading = false
  private saving = false
  private uploading = false
  private versionsLoading = false
  private togglingId: number | null = null

  private slotOrder: CaneAudioSlot[] = SLOTS
  private languages: CaneAudioLanguage[] = []
  private generic: CaneAudioSpecialPack = { activePack: null, versionCount: 0 }
  private englishPrimary: CaneAudioSpecialPack = { activePack: null, versionCount: 0 }

  private search = ''
  private showInactive = false

  private languageVisible = false
  private editingCode: string | null = null
  private languageForm = this.emptyLanguageForm()

  private versionsVisible = false
  private versionsCtx: { kind: CaneAudioPackKind, languageCode: string | null, slot: CaneAudioSlot | null } =
    { kind: 'language', languageCode: null, slot: null }

  private versions: CaneAudioPack[] = []

  private uploadVisible = false
  private uploadError = ''
  private uploadFile: File | null = null
  private uploadForm: { kind: CaneAudioPackKind, languageCode: string, slot: CaneAudioSlot | '', version: string, note: string } =
    { kind: 'language', languageCode: '', slot: '', version: '', note: '' }

  private get languageRules() {
    return {
      code: [{ required: true, message: this.$t('caneAudio.required'), trigger: 'blur' }],
      displayName: [{ required: true, message: this.$t('caneAudio.required'), trigger: 'blur' }],
      backendKey: [{ required: true, message: this.$t('caneAudio.required'), trigger: 'blur' }],
      otaFolder: [{ required: true, message: this.$t('caneAudio.required'), trigger: 'blur' }],
      manifestPrefix: [{ required: true, message: this.$t('caneAudio.required'), trigger: 'blur' }],
      synthesisCode: [{ required: true, message: this.$t('caneAudio.required'), trigger: 'blur' }]
    }
  }

  private get specialCards() {
    return [
      { kind: 'generic' as CaneAudioPackKind, data: this.generic },
      { kind: 'english_primary' as CaneAudioPackKind, data: this.englishPrimary }
    ]
  }

  private get visibleLanguages() {
    const q = this.search.trim().toLowerCase()
    return this.languages.filter((l) => {
      if (!this.showInactive && !l.isActive) return false
      if (!q) return true
      return [l.code, l.displayName, l.backendKey, l.otaFolder, ...l.aliases].some((v) => String(v).toLowerCase().includes(q))
    })
  }

  private get versionsTitle() {
    const c = this.versionsCtx
    if (c.kind !== 'language') return `${this.$t('caneAudio.versions')} · ${this.$t('caneAudio.kind_' + c.kind)}`
    return `${this.$t('caneAudio.versions')} · ${c.languageCode}`
  }

  private get uploadKindLabel() {
    return this.$t('caneAudio.kind_' + this.uploadForm.kind)
  }

  private get uploadReady() {
    const f = this.uploadForm
    if (!this.uploadFile || !f.version.trim()) return false
    if (f.kind === 'language' && (!f.languageCode || !f.slot)) return false
    return true
  }

  created() {
    this.fetch()
  }

  // ----------------------------------------------------------- data ----

  private async fetch() {
    this.loading = true
    try {
      const res = await getCaneAudioOverview()
      const data: CaneAudioOverview = res.data.data
      this.languages = data.languages
      this.generic = data.generic
      this.englishPrimary = data.englishPrimary
      if (data.slotOrder && data.slotOrder.length) this.slotOrder = data.slotOrder
    } catch (e) {
      // request.ts already showed the message
    } finally {
      this.loading = false
    }
  }

  private slotView(row: CaneAudioLanguage, slot: CaneAudioSlot) {
    return row.slots.find((s) => s.slot === slot) || null
  }

  private firstSlot(row: CaneAudioLanguage): CaneAudioSlot {
    const withVoice = this.slotOrder.find((s) => row.voices && row.voices[s])
    return withVoice || this.slotOrder[0]
  }

  private rowClass({ row }: { row: CaneAudioLanguage }) {
    return row.isActive ? '' : 'row-inactive'
  }

  // ------------------------------------------------------- language ----

  private emptyLanguageForm() {
    return {
      code: '', displayName: '', backendKey: '', otaFolder: '', manifestPrefix: '', synthesisCode: '',
      aliasesText: '', voices: { female: '', female2: '', male: '', male2: '' } as Record<CaneAudioSlot, string>,
      sortOrder: 0, isActive: true
    }
  }

  private openCreateLanguage() {
    this.editingCode = null
    this.languageForm = this.emptyLanguageForm()
    this.languageForm.sortOrder = this.languages.length + 1
    this.languageVisible = true
  }

  private openEditLanguage(row: CaneAudioLanguage) {
    this.editingCode = row.code
    this.languageForm = {
      code: row.code,
      displayName: row.displayName,
      backendKey: row.backendKey,
      otaFolder: row.otaFolder,
      manifestPrefix: row.manifestPrefix,
      synthesisCode: row.synthesisCode,
      aliasesText: row.aliases.join(', '),
      voices: {
        female: row.voices.female || '',
        female2: row.voices.female2 || '',
        male: row.voices.male || '',
        male2: row.voices.male2 || ''
      },
      sortOrder: row.sortOrder,
      isActive: row.isActive
    }
    this.languageVisible = true
  }

  private async saveLanguage() {
    const form = this.$refs.languageForm as any
    const valid = await form.validate().catch(() => false)
    if (!valid) return
    const f = this.languageForm
    const payload = {
      code: f.code.trim(),
      displayName: f.displayName.trim(),
      backendKey: f.backendKey.trim(),
      otaFolder: f.otaFolder.trim(),
      manifestPrefix: f.manifestPrefix.trim(),
      synthesisCode: f.synthesisCode.trim(),
      aliases: f.aliasesText.split(',').map((a) => a.trim()).filter(Boolean),
      voices: f.voices,
      sortOrder: f.sortOrder,
      isActive: f.isActive
    }
    this.saving = true
    try {
      if (this.editingCode) await updateCaneAudioLanguage(this.editingCode, payload)
      else await createCaneAudioLanguage(payload)
      this.$message.success(String(this.$t('caneAudio.saved')))
      this.languageVisible = false
      await this.fetch()
    } catch (e) {
      // message shown by request.ts
    } finally {
      this.saving = false
    }
  }

  // ------------------------------------------------------- versions ----

  private openVersions(kind: CaneAudioPackKind, languageCode: string | null, slot: CaneAudioSlot | null) {
    this.versionsCtx = { kind, languageCode, slot }
    this.versionsVisible = true
    this.fetchVersions()
  }

  private async fetchVersions() {
    const c = this.versionsCtx
    this.versionsLoading = true
    try {
      const params: any = { kind: c.kind }
      if (c.kind === 'language') {
        params.languageCode = c.languageCode
        params.slot = c.slot
      }
      const res = await listCaneAudioPacks(params)
      this.versions = res.data.data
    } catch (e) {
      this.versions = []
    } finally {
      this.versionsLoading = false
    }
  }

  private async setActive(row: CaneAudioPack, isActive: boolean) {
    this.togglingId = row.id
    try {
      await updateCaneAudioPack(row.id, { isActive })
      this.$message.success(String(this.$t('caneAudio.saved')))
      await Promise.all([this.fetchVersions(), this.fetch()])
    } catch (e) {
      // message shown by request.ts
    } finally {
      this.togglingId = null
    }
  }

  private async editNote(row: CaneAudioPack) {
    try {
      const { value } = await this.$prompt(String(this.$t('caneAudio.note')), String(this.$t('caneAudio.editNote')), {
        inputValue: row.note || '',
        confirmButtonText: String(this.$t('caneAudio.save')),
        cancelButtonText: String(this.$t('caneAudio.close'))
      }) as any
      await updateCaneAudioPack(row.id, { note: value || null })
      await this.fetchVersions()
    } catch (e) {
      // cancelled
    }
  }

  // --------------------------------------------------------- upload ----

  private openUpload(kind: CaneAudioPackKind, languageCode: string | null, slot: CaneAudioSlot | null) {
    this.uploadForm = { kind, languageCode: languageCode || '', slot: slot || '', version: '', note: '' }
    this.uploadFile = null
    this.uploadError = ''
    this.uploadVisible = true
    this.$nextTick(() => {
      const input = this.$refs.fileInput as HTMLInputElement | undefined
      if (input) input.value = ''
    })
  }

  private onFilePicked(ev: Event) {
    const input = ev.target as HTMLInputElement
    this.uploadFile = input.files && input.files.length ? input.files[0] : null
    this.uploadError = ''
  }

  private async doUpload() {
    if (!this.uploadFile) return
    const f = this.uploadForm
    this.uploading = true
    this.uploadError = ''
    try {
      await uploadCaneAudioPack({
        kind: f.kind,
        languageCode: f.kind === 'language' ? f.languageCode : undefined,
        slot: f.kind === 'language' ? f.slot : undefined,
        version: f.version.trim(),
        note: f.note.trim() || undefined
      }, this.uploadFile)
      this.$message.success(String(this.$t('caneAudio.uploaded')))
      this.uploadVisible = false
      await this.fetch()
      if (this.versionsVisible) await this.fetchVersions()
    } catch (e: any) {
      // The server's message is the useful part: which file, which field.
      const serverMsg = e && e.response && e.response.data && e.response.data.status && e.response.data.status.message
      this.uploadError = serverMsg || (e && e.message) || String(this.$t('caneAudio.failed'))
    } finally {
      this.uploading = false
    }
  }

  // ---------------------------------------------------------- utils ----

  private fmtSize(bytes: number) {
    if (!bytes && bytes !== 0) return ''
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
  }

  private shortSha(sha: string) {
    return sha ? sha.slice(0, 10) + '…' : ''
  }

  private fmtDate(iso: string) {
    if (!iso) return ''
    const d = new Date(iso)
    const p = (n: number) => String(n).padStart(2, '0')
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
  }
}
